import { useState } from 'react'
import { clsx } from 'clsx'
import { Scale, Filter, Clock, CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp } from 'lucide-react'

interface Regulation {
  id: string
  name: string
  jurisdiction: 'EU' | 'UK' | 'US'
  status: 'active' | 'upcoming' | 'enforcing'
  keyDates: { label: string; date: string }[]
  nextDeadline: string
  description: string
  impact: string
}

const REGULATIONS: Regulation[] = [
  {
    id: 'eu-ai-act',
    name: 'EU AI Act',
    jurisdiction: 'EU',
    status: 'enforcing',
    keyDates: [
      { label: 'Risk classification deadline', date: '2025-08-01' },
      { label: 'Full enforcement', date: '2026-08-01' },
    ],
    nextDeadline: '2026-08-01',
    description: 'World\'s first comprehensive AI law. Establishes risk-based framework for AI systems with tiered obligations from minimal to unacceptable risk.',
    impact: 'Requires AI risk assessments, transparency obligations, conformity assessments for high-risk AI. Bans certain AI practices outright.',
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    jurisdiction: 'EU',
    status: 'active',
    keyDates: [
      { label: 'In force since', date: '2018-05-25' },
      { label: 'Next major review', date: '2025-12-31' },
    ],
    nextDeadline: '2025-12-31',
    description: 'General Data Protection Regulation governing personal data processing across the EU/EEA.',
    impact: 'Strict consent requirements, data subject rights, DPO appointments, cross-border transfer restrictions. Fines up to 4% of global revenue.',
  },
  {
    id: 'eu-dma',
    name: 'EU Digital Markets Act (DMA)',
    jurisdiction: 'EU',
    status: 'enforcing',
    keyDates: [
      { label: 'In force', date: '2023-05-02' },
      { label: 'Gatekeeper compliance ongoing', date: '2024-03-06' },
    ],
    nextDeadline: '2024-03-06',
    description: 'Regulates large online platforms designated as "gatekeepers" to ensure fair and contestable digital markets.',
    impact: 'Interoperability requirements, ban on self-preferencing, data portability mandates. Affects app stores, browsers, messaging, search.',
  },
  {
    id: 'eu-dsa',
    name: 'EU Digital Services Act (DSA)',
    jurisdiction: 'EU',
    status: 'active',
    keyDates: [
      { label: 'Full enforcement', date: '2024-02-17' },
    ],
    nextDeadline: '2024-02-17',
    description: 'Comprehensive regulation of digital services including online platforms, marketplaces, and intermediaries.',
    impact: 'Content moderation transparency, illegal content reporting, risk assessments for very large platforms. Algorithmic accountability.',
  },
  {
    id: 'nis2',
    name: 'NIS2 Directive',
    jurisdiction: 'EU',
    status: 'enforcing',
    keyDates: [
      { label: 'Transposition deadline', date: '2024-10-17' },
      { label: 'Enforcement ongoing', date: '2024-10-18' },
    ],
    nextDeadline: '2024-10-17',
    description: 'Updated Network and Information Security directive expanding cybersecurity obligations across critical sectors.',
    impact: 'Mandatory incident reporting (24h), supply chain security, management accountability, cross-border cooperation requirements.',
  },
  {
    id: 'eu-cra',
    name: 'EU Cyber Resilience Act',
    jurisdiction: 'EU',
    status: 'upcoming',
    keyDates: [
      { label: 'Expected enforcement', date: '2027-06-01' },
    ],
    nextDeadline: '2027-06-01',
    description: 'Mandatory cybersecurity requirements for products with digital elements sold in the EU.',
    impact: 'Security-by-design obligations, vulnerability handling, software bill of materials (SBOM), CE marking for digital products.',
  },
  {
    id: 'uk-osa',
    name: 'UK Online Safety Act',
    jurisdiction: 'UK',
    status: 'enforcing',
    keyDates: [
      { label: 'Phased enforcement start', date: '2024-01-01' },
      { label: 'Full enforcement', date: '2025-12-31' },
    ],
    nextDeadline: '2025-12-31',
    description: 'UK legislation imposing duty of care on online services to protect users from harmful content.',
    impact: 'Age verification requirements, illegal content duties, transparency reporting, risk assessments for user-to-user and search services.',
  },
  {
    id: 'us-ai-eo',
    name: 'US AI Executive Order',
    jurisdiction: 'US',
    status: 'active',
    keyDates: [
      { label: 'Signed', date: '2023-10-30' },
      { label: 'Ongoing implementation', date: '2025-12-31' },
    ],
    nextDeadline: '2025-12-31',
    description: 'Executive order establishing safety, security, and trustworthiness standards for AI development and deployment.',
    impact: 'Red-teaming requirements for frontier models, watermarking guidance, federal procurement standards, workforce impact assessments.',
  },
  {
    id: 'eu-data-act',
    name: 'EU Data Act',
    jurisdiction: 'EU',
    status: 'upcoming',
    keyDates: [
      { label: 'Application date', date: '2025-09-12' },
    ],
    nextDeadline: '2025-09-12',
    description: 'Regulates access to and use of data generated by connected products and related services.',
    impact: 'Data sharing obligations for IoT manufacturers, cloud switching rights, interoperability requirements, government data access rules.',
  },
  {
    id: 'pci-dss-4',
    name: 'PCI DSS v4.0',
    jurisdiction: 'US',
    status: 'enforcing',
    keyDates: [
      { label: 'Full compliance deadline', date: '2025-03-31' },
    ],
    nextDeadline: '2025-03-31',
    description: 'Payment Card Industry Data Security Standard version 4.0 with updated security requirements for cardholder data.',
    impact: 'Enhanced authentication requirements, targeted risk analysis, customized security approach option, expanded encryption and MFA mandates.',
  },
]

const JURISDICTION_COLOURS: Record<string, string> = {
  EU: 'badge-blue',
  UK: 'badge-purple',
  US: 'badge-amber',
}

const JURISDICTION_OPTIONS = [
  { value: 'all', label: 'All Jurisdictions' },
  { value: 'EU', label: 'EU' },
  { value: 'UK', label: 'UK' },
  { value: 'US', label: 'US' },
]

const TODAY = new Date('2026-04-15')

function deadlineStatus(dateStr: string): 'compliant' | 'approaching' | 'past' {
  const d = new Date(dateStr)
  const diff = d.getTime() - TODAY.getTime()
  const daysDiff = diff / (1000 * 60 * 60 * 24)
  if (daysDiff < 0) return 'past'
  if (daysDiff < 90) return 'approaching'
  return 'compliant'
}

function deadlineColour(status: string): string {
  if (status === 'compliant') return 'text-green'
  if (status === 'approaching') return 'text-amber'
  return 'text-red'
}

function deadlineBg(status: string): string {
  if (status === 'compliant') return 'border-green/30'
  if (status === 'approaching') return 'border-amber/30'
  return 'border-red/30'
}

function deadlineIcon(status: string) {
  if (status === 'compliant') return <CheckCircle size={14} className="text-green" />
  if (status === 'approaching') return <AlertTriangle size={14} className="text-amber" />
  return <XCircle size={14} className="text-red" />
}

function statusBadge(s: string): string {
  if (s === 'active') return 'badge-green'
  if (s === 'upcoming') return 'badge-blue'
  return 'badge-amber'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysUntil(dateStr: string): number {
  const d = new Date(dateStr)
  return Math.round((d.getTime() - TODAY.getTime()) / (1000 * 60 * 60 * 24))
}

export default function CompliancePage() {
  const [jurisdictionFilter, setJurisdictionFilter] = useState('all')
  const [sortAsc, setSortAsc] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const filtered = REGULATIONS
    .filter(r => jurisdictionFilter === 'all' || r.jurisdiction === jurisdictionFilter)
    .sort((a, b) => {
      const da = new Date(a.nextDeadline).getTime()
      const db = new Date(b.nextDeadline).getTime()
      return sortAsc ? da - db : db - da
    })

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Scale size={24} className="text-blue" /> Compliance Tracker
          </h1>
          <p className="text-sm text-text-secondary mt-1">Major tech regulations, deadlines, and enforcement status</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          title=""
          value={jurisdictionFilter}
          onChange={e => setJurisdictionFilter(e.target.value)}
          className="input w-auto py-2 text-xs"
        >
          {JURISDICTION_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setSortAsc(!sortAsc)}
          className="btn-secondary text-xs flex items-center gap-1"
        >
          <Clock size={14} />
          Deadline {sortAsc ? 'Soonest' : 'Latest'}
          {sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        <span className="text-[10px] text-text-muted ml-auto">{filtered.length} regulation{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Timeline summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Past Deadline', count: filtered.filter(r => deadlineStatus(r.nextDeadline) === 'past').length, colour: 'text-red', bg: 'bg-red/10' },
          { label: 'Approaching', count: filtered.filter(r => deadlineStatus(r.nextDeadline) === 'approaching').length, colour: 'text-amber', bg: 'bg-amber/10' },
          { label: 'Compliant Window', count: filtered.filter(r => deadlineStatus(r.nextDeadline) === 'compliant').length, colour: 'text-green', bg: 'bg-green/10' },
        ].map(s => (
          <div key={s.label} className={clsx('card p-4 text-center', s.bg)}>
            <div className={clsx('text-2xl font-bold', s.colour)}>{s.count}</div>
            <div className="text-[10px] text-text-muted mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {filtered.map(reg => {
          const ds = deadlineStatus(reg.nextDeadline)
          const days = daysUntil(reg.nextDeadline)
          const isExpanded = expandedId === reg.id
          return (
            <div
              key={reg.id}
              className={clsx('card p-4 border-l-4 transition-all', deadlineBg(ds))}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={clsx('text-[10px]', JURISDICTION_COLOURS[reg.jurisdiction])}>{reg.jurisdiction}</span>
                    <span className={clsx('text-[10px]', statusBadge(reg.status))}>{reg.status}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary leading-snug">{reg.name}</h3>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">{reg.description}</p>

                  {/* Key dates */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {reg.keyDates.map((kd, i) => (
                      <div key={i} className="flex items-center gap-1 text-[10px] text-text-muted">
                        <Clock size={10} />
                        <span className="text-text-secondary">{kd.label}:</span>
                        <span className="font-medium text-text-primary">{formatDate(kd.date)}</span>
                      </div>
                    ))}
                  </div>

                  {isExpanded && (
                    <div className="mt-3 p-3 bg-card-alt/50 rounded text-xs text-text-secondary leading-relaxed">
                      <span className="font-medium text-text-primary">Impact on tech companies: </span>
                      {reg.impact}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    {deadlineIcon(ds)}
                    <span className={clsx('text-xs font-semibold tabular-nums', deadlineColour(ds))}>
                      {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                    </span>
                  </div>
                  <div className="text-[10px] text-text-muted">{formatDate(reg.nextDeadline)}</div>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : reg.id)}
                    className="text-[10px] text-blue hover:underline mt-1"
                  >
                    {isExpanded ? 'Less' : 'Impact'}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
